import React from "react";
import { Box, Chip, Grid, Stack, Typography, useTheme } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { AnalyticsContext } from "../context/analyticsContext";
import { ISearchResults } from "../interface/iSearchResults";
import { IAppointment } from "../interface/iAppointment";

const BAR_COLORS = [
  "#42a5f5",
  "#26c6da",
  "#66bb6a",
  "#ffca28",
  "#ffa726",
  "#ef5350",
  "#ab47bc",
  "#ec407a",
];

const CoachingCountDistribution = (): React.JSX.Element => {
  const theme = useTheme();
  const { searchResults }: { searchResults: ISearchResults } =
    React.useContext(AnalyticsContext);

  const { xLabels, series, totalParticipants } = React.useMemo(() => {
    const appointments = searchResults.appointmentList?.appointments ?? [];

    const relevant = appointments.filter(
      (a: IAppointment) => (a.status === "G" || a.status === "V") && a.email,
    );

    const countByEmail = new Map<string, number>();
    relevant.forEach((a: IAppointment) => {
      countByEmail.set(a.email, (countByEmail.get(a.email) ?? 0) + 1);
    });

    const total = countByEmail.size;
    if (total === 0) return { xLabels: [], series: [], totalParticipants: 0 };

    const buckets = new Map<number, number>();
    countByEmail.forEach((count) => {
      buckets.set(count, (buckets.get(count) ?? 0) + 1);
    });

    const rawSorted = Array.from(buckets.entries()).sort(([a], [b]) => a - b);

    // Always show at least 4 bars so the chart doesn't look odd with a single full-width bar
    const MIN_BARS = 4;
    const maxCount = rawSorted.length > 0 ? rawSorted[rawSorted.length - 1][0] : 0;
    const neededCount = Math.max(maxCount, MIN_BARS);
    const sorted: [number, number][] = Array.from({ length: neededCount }, (_, i) => [
      i + 1,
      buckets.get(i + 1) ?? 0,
    ]);

    const labels = sorted.map(([count, participants]) => {
      const base = count === 1 ? "1 Termin" : `${count} Termine`;
      if (participants === 0) return base;
      const pct = ((participants / total) * 100).toFixed(1);
      return `${base} (${pct} %)`;
    });

    const chartSeries = sorted.map(([, participants], index) => ({
      data: sorted.map((_, i) => (i === index ? participants : 0)),
      color: BAR_COLORS[index % BAR_COLORS.length],
      stack: "distribution",
      valueFormatter: (value: number) =>
        value > 0
          ? `${value} (${((value / total) * 100).toFixed(1)} %)`
          : "",
    }));

    return { xLabels: labels, series: chartSeries, totalParticipants: total };
  }, [searchResults]);

  const pieData = React.useMemo(() => {
    const meta = searchResults.appointmentList?.meta;
    if (!meta) return [];

    return [
      { id: 0, value: meta.active, label: "Aktiv", color: theme.palette.success.main },
      { id: 1, value: meta.done, label: "Durchgeführt", color: theme.palette.secondary.main },
      { id: 2, value: meta.missed, label: "Versäumt", color: theme.palette.error.main },
      { id: 3, value: meta.cancelled, label: "Storniert", color: theme.palette.warning.main },
      { id: 4, value: meta.free, label: "Frei", color: theme.palette.info.main },
    ].filter((d) => d.value > 0);
  }, [searchResults, theme.palette]);

  if (xLabels.length === 0 && pieData.length === 0) return <></>;

  return (
    <Box sx={{ mt: 4 }}>
      <Grid container spacing={4}>
        {/* Bar chart – coaching count distribution */}
        <Grid item xs={12} md={6}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6">
              Coachings pro Teilnehmer
            </Typography>
            <Chip
              label={`${totalParticipants} Teilnehmer`}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Stack>
          <BarChart
            height={300}
            series={series}
            xAxis={[{
              scaleType: "band",
              data: xLabels,
              tickLabelStyle: { angle: -35, textAnchor: "end", fontSize: 11 },
            }]}
            margin={{ left: 60, right: 20, top: 20, bottom: 70 }}
            tooltip={{ trigger: "item" }}
            slotProps={{ legend: { hidden: true } }}
          />
        </Grid>

        {/* Pie chart – appointment status distribution */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Terminübersicht
          </Typography>
          <PieChart
            height={300}
            series={[
              {
                data: pieData,
                innerRadius: 30,
                outerRadius: 100,
                paddingAngle: 5,
                cornerRadius: 5,
              },
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CoachingCountDistribution;
